/**
 * 049 T014 / T015 / T016 — expenses, and native receipt capture.
 *
 * The capture flow saves first and reads second. You get a row the instant you press Save, and the
 * extracted fields arrive into it afterwards. That ordering is not a nicety: it is what stops a
 * model outage or an empty credit balance from eating a receipt somebody already threw away.
 */
import { useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, Camera, Download, Trash2, Loader2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import {
  Page, PageHeader, Card, CardHeader, Toolbar, btnPrimary, btnSecondary, btnGhost,
  EmptyState, SkeletonRows, StatusPill, SearchInput, hoverActions, useConfirm,
} from '@/components/rm'
import { Modal, Field, Input, Select, Textarea } from '@/components/rm/form'
import { money } from '@/lib/format'
import { todayIso } from '@/lib/today'
import { useTeam } from '@/hooks/use-company'
import { useJobs } from '@/hooks/use-jobs'
import { phFormat, phDay } from '@/lib/ph'
import {
  useExpenses, useExpenseCategories, useExpenseMutations, useDraftInvoicesForJob, expensesToCsv,
  type Expense, type ExpenseFilters,
} from '@/hooks/use-expenses'

export const Route = createFileRoute('/_app/expenses')({ component: ExpensesPage })

const firstOfMonth = () => `${todayIso().slice(0, 8)}01`

function ExpensesPage() {
  const [filters, setFilters] = useState<ExpenseFilters>({ from: firstOfMonth(), to: todayIso() })
  const [search, setSearch] = useState('')
  const [capturing, setCapturing] = useState(false)
  const [editing, setEditing] = useState<Expense | null>(null)

  const { data: rows, isLoading } = useExpenses({ ...filters, search: search || undefined })
  const { data: categories } = useExpenseCategories()
  const { members } = useTeam()
  const { remove } = useExpenseMutations()
  const [confirm, confirmDialog] = useConfirm()

  const nameFor = (userId: string) => {
    const m = members.find((x) => x.id === userId)
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Someone' : 'Someone'
  }

  const totals = useMemo(() => {
    const list = rows ?? []
    return {
      count: list.length,
      total: list.reduce((s, r) => s + Number(r.total ?? 0), 0),
      gst: list.reduce((s, r) => s + Number(r.tax ?? 0), 0),
      billable: list.filter((r) => r.billable).reduce((s, r) => s + Number(r.total ?? 0), 0),
    }
  }, [rows])

  const exportCsv = () => {
    if (!rows?.length) return toast.error('Nothing to export')
    const csv = expensesToCsv(rows, nameFor)
    // A BOM, so Excel reads it as UTF-8 rather than mangling every supplier with an accent.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `expenses-${filters.from ?? 'all'}-to-${filters.to ?? 'now'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Money"
        title="Expenses"
        subtitle="Photograph a receipt and the figures fill themselves in. Correct anything that is wrong."
        actions={
          <>
            <button type="button" className={btnSecondary} onClick={exportCsv}>
              <Download className="h-4 w-4" /> Export
            </button>
            <button type="button" className={btnPrimary} onClick={() => setCapturing(true)}>
              <Plus className="h-4 w-4" /> New expense
            </button>
          </>
        }
      />

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Expenses" value={String(totals.count)} />
        <Stat label="Total spent" value={money(totals.total)} />
        <Stat label="VAT" value={money(totals.gst)} />
        <Stat label="Rechargeable" value={money(totals.billable)} />
      </div>

      <Toolbar className="mt-5">
        <input
          type="date" aria-label="From"
          className="h-9 rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary"
          value={filters.from ?? ''} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
        />
        <input
          type="date" aria-label="To"
          className="h-9 rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary"
          value={filters.to ?? ''} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
        />
        <select
          aria-label="Category"
          className="h-9 rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary"
          value={filters.category ?? ''} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value || undefined }))}
        >
          <option value="">Every category</option>
          {(categories ?? []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select
          aria-label="Captured by"
          className="h-9 rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary"
          value={filters.userId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value || undefined }))}
        >
          <option value="">Everyone</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>
          ))}
        </select>
        <select
          aria-label="Rechargeable"
          className="h-9 rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary"
          value={filters.billable == null ? '' : String(filters.billable)}
          onChange={(e) => setFilters((f) => ({ ...f, billable: e.target.value === '' ? null : e.target.value === 'true' }))}
        >
          <option value="">Billable or not</option>
          <option value="true">Rechargeable</option>
          <option value="false">Not rechargeable</option>
        </select>
        <div className="min-w-[180px] flex-1">
          <SearchInput value={search} onChange={setSearch} placeholder="Supplier…" />
        </div>
      </Toolbar>

      <Card className="mt-4">
        <CardHeader title="Spend" count={rows?.length} />
        {isLoading ? (
          <SkeletonRows count={5} />
        ) : !rows?.length ? (
          <EmptyState
            label="Nothing here for these dates. Photograph a receipt and the rest fills itself in."
            cta="New expense"
            onCta={() => setCapturing(true)}
          />
        ) : (
          <ul className="divide-y divide-token">
            {rows.map((r) => (
              <li
                key={r.id}
                className="group flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-token-hover"
                onClick={() => setEditing(r)}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-token-primary">
                    {r.supplier_name ?? 'Unnamed supplier'}
                  </p>
                  <p className="truncate text-[12px] text-token-secondary">
                    {phFormat(phDay(r.expense_date), 'date', { day: 'numeric', month: 'short' })}
                    {' · '}{r.category}
                    {r.billable && ' · rechargeable'}
                    {' · '}{nameFor(r.user_id)}
                  </p>
                </div>
                <OcrBadge expense={r} />
                <span className="shrink-0 text-[14px] font-semibold tabular-nums text-token-primary">{money(r.total)}</span>
                <div className={`flex items-center gap-1 ${hoverActions}`} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button" className={btnGhost} aria-label="Delete this expense"
                    onClick={async () => {
                      const ok = await confirm({ title: 'Delete this expense?', tone: 'danger', confirmLabel: 'Delete' })
                      if (ok) remove.mutate(r.id, { onSuccess: () => toast.success('Deleted') })
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CaptureModal open={capturing} onClose={() => setCapturing(false)} />
      <EditModal expense={editing} onClose={() => setEditing(null)} />
      {confirmDialog}
    </Page>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[12px] border border-token bg-token-surface px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">{label}</p>
      <p className="font-display mt-1 text-[17px] font-semibold text-token-primary">{value}</p>
    </div>
  )
}

/** What happened to the photo, in words a trade person can act on. */
function OcrBadge({ expense }: { expense: Expense }) {
  if (!expense.receipt_path) return null
  const meta = (expense.ocr_meta ?? {}) as Record<string, unknown>
  if (expense.ocr_status === 'pending') {
    return <span className="hidden shrink-0 items-center gap-1 text-[11.5px] text-token-secondary sm:inline-flex">
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Reading receipt…
    </span>
  }
  if (expense.ocr_status === 'skipped') {
    return <StatusPill status="draft" label={meta.reason === 'no_credits' ? 'No AI credits' : 'Not read'} />
  }
  if (expense.ocr_status === 'failed') return <StatusPill status="failed" label="Could not read" />
  if (meta.totals_disagree === true) {
    return <span className="hidden shrink-0 items-center gap-1 text-[11.5px] font-medium text-token-warning sm:inline-flex">
      <AlertTriangle className="h-3 w-3" aria-hidden /> Figures disagree
    </span>
  }
  return null
}

/**
 * Capture. Save is enabled the moment there is a photo or an amount — waiting for extraction
 * before allowing a save is the failure this whole flow is arranged to avoid.
 */
function CaptureModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { create, attachReceipt, extract } = useExpenseMutations()
  const { data: categories } = useExpenseCategories()
  const { jobs } = useJobs()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [form, setForm] = useState({
    supplier_name: '', total: '', expense_date: todayIso(),
    category: 'Other', task_id: '', billable: false, notes: '',
  })

  const reset = () => {
    setFile(null); setPreview(null); setBusy(null)
    setForm({ supplier_name: '', total: '', expense_date: todayIso(), category: 'Other', task_id: '', billable: false, notes: '' })
  }

  const pick = (f: File | null) => {
    setFile(f)
    setPreview(f ? URL.createObjectURL(f) : null)
  }

  const save = async () => {
    try {
      setBusy('Saving…')
      const expense = await create.mutateAsync({
        supplier_name: form.supplier_name || null,
        total: Number(form.total) || 0,
        expense_date: form.expense_date,
        category: form.category,
        task_id: form.task_id ? Number(form.task_id) : null,
        billable: form.billable,
        notes: form.notes || null,
      })
      // Saved. From here nothing can lose it — every failure below is reported and the row stays.
      if (file) {
        setBusy('Uploading the photo…')
        await attachReceipt.mutateAsync({ expenseId: expense.id, file })
        setBusy('Reading the receipt…')
        const r = await extract.mutateAsync(expense.id)
        if (r.ocr_status === 'done') toast.success('Receipt read. Check the figures.')
        else if (r.reason === 'no_credits') toast.warning('Saved. No AI credits left, so type the figures in.')
        else toast.warning('Saved, but the receipt could not be read. Type the figures in.')
      } else {
        toast.success('Expense saved')
      }
      reset(); onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save')
      setBusy(null)
    }
  }

  return (
    <Modal open={open} onClose={() => { reset(); onClose() }} title="New expense" wide>
      <div className="space-y-4">
        <div>
          <input
            ref={fileRef} type="file" accept="image/*" capture="environment" className="sr-only"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
          {preview ? (
            <div className="relative overflow-hidden rounded-[12px] border border-token">
              <img src={preview} alt="The receipt you photographed" className="max-h-[220px] w-full object-contain bg-token-hover" />
              <button
                type="button" className={`${btnSecondary} absolute right-2 top-2`}
                onClick={() => { pick(null); if (fileRef.current) fileRef.current.value = '' }}
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="flex w-full flex-col items-center gap-1.5 rounded-[12px] border border-dashed border-token bg-token-hover px-4 py-7 text-token-secondary hover:bg-token-surface"
              onClick={() => fileRef.current?.click()}
            >
              <Camera className="h-6 w-6" aria-hidden />
              <span className="text-[13.5px] font-semibold text-token-primary">Photograph the receipt</span>
              <span className="text-[12px]">Or save without one and type the figures.</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Supplier">
            <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Left blank, the photo fills it" />
          </Field>
          <Field label="Total">
            <Input type="number" step="0.01" min="0" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} placeholder="0.00" />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </Field>
          <Field label="Category">
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {(categories ?? []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Job">
            <Select value={form.task_id} onChange={(e) => setForm({ ...form, task_id: e.target.value })}>
              <option value="">Not on a job</option>
              {jobs.slice(0, 200).map((j) => <option key={j.id} value={j.id}>{j.name ?? `Job ${j.id}`}</option>)}
            </Select>
          </Field>
          <Field label="Recharge to the client">
            <label className="flex h-9 items-center gap-2 text-[13.5px] text-token-primary">
              <input type="checkbox" className="h-4 w-4" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} />
              Add it to their invoice
            </label>
          </Field>
        </div>

        <Field label="Notes">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>

        <div className="flex items-center justify-end gap-2">
          {busy && <span className="mr-auto text-[12.5px] text-token-secondary">{busy}</span>}
          <button type="button" className={btnSecondary} onClick={() => { reset(); onClose() }}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={!!busy || (!file && !form.total)} onClick={save}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** Correcting a figure is the point. What the model said is kept in ocr_meta either way. */
function EditModal({ expense, onClose }: { expense: Expense | null; onClose: () => void }) {
  const { update, addToInvoice } = useExpenseMutations()
  const drafts = useDraftInvoicesForJob(expense?.task_id)
  const { data: categories } = useExpenseCategories()
  const [form, setForm] = useState<Partial<Expense>>({})
  const merged = { ...expense, ...form } as Expense

  if (!expense) return null
  const meta = (expense.ocr_meta ?? {}) as Record<string, unknown>
  const said = meta.extracted as Record<string, unknown> | undefined

  return (
    <Modal open={!!expense} onClose={() => { setForm({}); onClose() }} title="Expense" wide>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Supplier">
            <Input value={merged.supplier_name ?? ''} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
          </Field>
          <Field label="Date">
            <Input type="date" value={merged.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </Field>
          <Field label="Subtotal">
            <Input type="number" step="0.01" value={merged.subtotal ?? ''} onChange={(e) => setForm({ ...form, subtotal: Number(e.target.value) })} />
          </Field>
          <Field label="VAT">
            <Input type="number" step="0.01" value={merged.tax ?? ''} onChange={(e) => setForm({ ...form, tax: Number(e.target.value) })} />
          </Field>
          <Field label="Total">
            <Input type="number" step="0.01" value={merged.total ?? 0} onChange={(e) => setForm({ ...form, total: Number(e.target.value) })} />
          </Field>
          <Field label="Category">
            <Select value={merged.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {(categories ?? []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        {/* 049 T029. Rechargeable, on a job, not yet invoiced: the one state where this is offered.
            The charge is total + markup; the COST stays what was spent, which is what the profit
            card reads. */}
        {expense.billable && expense.task_id && !expense.invoice_line_item_id && (
          <div className="rounded-[10px] border border-token bg-token-hover p-3">
            <p className="text-[13px] font-semibold text-token-primary">Recharge this to the client</p>
            <p className="mt-0.5 text-[12px] leading-snug text-token-secondary">
              {money(merged.total)}
              {merged.markup_pct ? ` plus ${merged.markup_pct}% is ${money(Number(merged.total) * (1 + Number(merged.markup_pct) / 100))}` : ' at cost'}.
              The job's cost does not change — only what the client is charged.
            </p>
            {drafts.data?.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {drafts.data.map((inv) => (
                  <button
                    key={inv.id} type="button" className={btnSecondary}
                    disabled={addToInvoice.isPending}
                    onClick={async () => {
                      try {
                        const r = await addToInvoice.mutateAsync({ expenseId: expense.id, invoiceId: inv.id })
                        toast.success(r.already ? 'It was already on that invoice' : `Added ${money(Number(r.charged ?? 0))} to the invoice`)
                        onClose()
                      } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not add it') }
                    }}
                  >
                    Add to {inv.invoice_number ?? `invoice ${inv.id}`}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[12px] text-token-muted">
                This job has no draft invoice. Create one and it will appear here — a sent invoice cannot be changed.
              </p>
            )}
          </div>
        )}

        {expense.invoice_line_item_id && (
          <p className="rounded-[10px] border border-token bg-token-success-surface px-3 py-2 text-[12.5px] text-token-success">
            Already on an invoice. It cannot be added twice.
          </p>
        )}

        {meta.totals_disagree === true && (
          <p className="rounded-[10px] border border-token bg-token-warning-surface px-3 py-2.5 text-[12.5px] leading-snug text-token-warning">
            The subtotal and VAT on this receipt do not add up to the total. All three were kept exactly as printed —
            correct whichever one is wrong rather than trusting the sum.
          </p>
        )}

        {said && (
          <details className="rounded-[10px] border border-token px-3 py-2">
            <summary className="cursor-pointer text-[12.5px] font-semibold text-token-primary">What was read off the photo</summary>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-token-secondary">
              {Object.entries(said).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-token-muted">{k.replace(/_/g, ' ')}</dt>
                  <dd>{v == null ? '—' : String(v)}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-[11.5px] text-token-muted">
              Kept separately from the figures above, so a correction months later can still be explained.
            </p>
          </details>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={() => { setForm({}); onClose() }}>Cancel</button>
          <button
            type="button" className={btnPrimary}
            onClick={async () => {
              try {
                await update.mutateAsync({ id: expense.id, ...form })
                toast.success('Saved'); setForm({}); onClose()
              } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save') }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  )
}
